import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import toast from "react-hot-toast";

import { RefreshCw } from "lucide-react";

const API_URL = " https://axtelica-api.vercel.app";

const DemoForm = ({
  title = "Ready to unlock the power of your data?",
  description = "Request a demo with an Axtelica AI product expert to see how you can:",
  points = [],
}) => {
  

  // ================= FORM =================
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    phone: "",
    employees: "",
    country: "",
  });

  // ================= STATES =================
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // CAPTCHA
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // ✅ IMPORTANT FIX
  // useRef prevents stale captcha token issue
  const captchaTokenRef = useRef("");

  // ================= FETCH CAPTCHA =================
  const fetchCaptcha = useCallback(async () => {
    try {
      setCaptchaLoading(true);

      // clear old captcha
      setCaptchaAnswer("");

      const res = await fetch(`${API_URL}/api/captcha`, {
        method: "GET",
        cache: "no-store",
      });

      const data = await res.json();

      if (data.success) {
        setCaptchaImage(data.captcha_image);

        // ✅ always latest token
        captchaTokenRef.current = data.captcha_token;
      } else {
        toast.error("Failed to load CAPTCHA");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // ================= LOAD CAPTCHA =================
  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });

    setErrors((prev) => ({
      ...prev,
      [e.target.name]: false,
      global: "",
    }));
  };

  // ================= VALIDATION =================
  const validate = () => {
    let newErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = true;
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = true;
    }

    if (!form.company.trim()) {
      newErrors.company = true;
    }

    if (
      !form.email.trim() ||
      !/^\S+@\S+\.\S+$/.test(form.email)
    ) {
      newErrors.email = true;
    }

    if (
      !form.phone.trim() ||
      !/^\d{10}$/.test(form.phone)
    ) {
      newErrors.phone = true;
    }

    if (!form.employees.trim()) {
      newErrors.employees = true;
    }

    if (!form.country.trim()) {
      newErrors.country = true;
    }

    // CAPTCHA
    if (!captchaAnswer.trim()) {
      newErrors.captcha = true;
    }

    return newErrors;
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({});

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors({
        ...validationErrors,
        global: "Please fill all required fields correctly",
      });

      toast.error("Please fix the form");
      return;
    }

    // ✅ latest captcha token
    const currentCaptchaToken = captchaTokenRef.current;

    if (!currentCaptchaToken) {
      toast.error("CAPTCHA expired. Please refresh.");
      fetchCaptcha();
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...form,

        // ✅ IMPORTANT
        captchaAnswer: captchaAnswer
          .trim()
          .toUpperCase(),

        captchaToken: currentCaptchaToken,
      };

      const res = await fetch(
        `${API_URL}/api/product`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      // ================= CAPTCHA FAIL =================
      if (!res.ok) {
        if (
          data.error
            ?.toLowerCase()
            .includes("captcha")
        ) {
          // refresh captcha automatically
          fetchCaptcha();
        }

        throw new Error(
          data.error || "Something went wrong"
        );
      }

      // ================= SUCCESS =================
      toast.success(
        "Demo request sent successfully!"
      );

      // RESET FORM
      setForm({
        firstName: "",
        lastName: "",
        company: "",
        email: "",
        phone: "",
        employees: "",
        country: "",
      });

      setCaptchaAnswer("");

      setErrors({});

      // new captcha after submit
      fetchCaptcha();

    } catch (err) {
      console.error(err);

      toast.error(
        err.message || "Server error"
      );
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-10 bg-white">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

        {/* LEFT CONTENT */}
        <div className="max-w-xl">

          <h1 className="text-[clamp(30px,5vw,50px)]  text-gray-900 mb-6 leading-tight">
            {title}
          </h1>

          <p className="text-gray-700 mb-8 text-lg leading-relaxed">
            {description}
          </p>

          <ul className="space-y-4 text-gray-700 text-lg">
            {points.map((item, i) => (
              <li
                key={i}
                className="flex gap-3"
              >
                <span className="text-[#FF3366] font-bold">
                  ✓
                </span>

                {item}
              </li>
            ))}
          </ul>

        </div>

        {/* FORM */}
        <div className="bg-white border border-gray-200 rounded-3xl p-5 sm:p-8 shadow-xl">

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 text-black"
          >

            {/* FIRST NAME */}
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder="First Name*"
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm ${
                errors.firstName
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {/* LAST NAME */}
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Last Name*"
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm ${
                errors.lastName
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {/* COMPANY */}
            <input
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder="Company*"
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm ${
                errors.company
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {/* EMAIL */}
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Business Email*"
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm ${
                errors.email
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {/* PHONE */}
            <input
              name="phone"
              value={form.phone}
              onChange={(e) => {
                const value = e.target.value.replace(
                  /\D/g,
                  ""
                );

                if (value.length <= 10) {
                  setForm({
                    ...form,
                    phone: value,
                  });
                }

                setErrors((prev) => ({
                  ...prev,
                  phone: false,
                }));
              }}
              placeholder="Phone*"
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm ${
                errors.phone
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />

            {/* EMPLOYEES */}
            <select
              name="employees"
              value={form.employees}
              onChange={handleChange}
              className={`w-full border px-4 py-3 rounded-xl outline-none text-sm bg-white ${
                errors.employees
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              <option value="">
                Employees*
              </option>

              <option value="1-10">
                1-10
              </option>

              <option value="10-50">
                10-50
              </option>

              <option value="50+">
                50+
              </option>
            </select>

            {/* COUNTRY */}
            <div className="md:col-span-2">

              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                className={`w-full border px-4 py-3 rounded-xl outline-none text-sm bg-white ${
                  errors.country
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">
                  Country*
                </option>

                <option value="India">
                  India
                </option>

                <option value="USA">
                  USA
                </option>

              </select>

            </div>

            {/* CAPTCHA */}
            <div className="md:col-span-2">

              <label className="text-sm font-medium mb-2 block">
                Security Code*
              </label>

              <div className="flex items-center gap-3 mb-3">

                {/* CAPTCHA IMAGE */}
                <div
                  className="border border-gray-300 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center"
                  style={{
                    width: 220,
                    height: 70,
                  }}
                >

                  {captchaLoading ? (
                    <span className="text-gray-400 text-sm">
                      Loading...
                    </span>
                  ) : captchaImage ? (
                    <img
                      src={captchaImage}
                      alt="CAPTCHA"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">
                      Failed to load
                    </span>
                  )}

                </div>

                {/* REFRESH */}
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  disabled={captchaLoading}
                  className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center hover:border-[#FF3366] hover:text-[#FF3366] transition"
                >
                  <RefreshCw
                    size={16}
                    className={
                      captchaLoading
                        ? "animate-spin"
                        : ""
                    }
                  />
                </button>

              </div>

              {/* CAPTCHA INPUT */}
              <input
                type="text"
                value={captchaAnswer}
                onChange={(e) => {
                  setCaptchaAnswer(
                    e.target.value
                      .toUpperCase()
                      .replace(/\s/g, "")
                  );

                  setErrors((prev) => ({
                    ...prev,
                    captcha: false,
                  }));
                }}
                placeholder="Enter CAPTCHA"
                autoComplete="off"
                maxLength={5}
                className={`w-full border px-4 py-3 rounded-xl uppercase tracking-[4px] outline-none text-sm ${
                  errors.captcha
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

            </div>

            {/* SUBMIT */}
            <div className="md:col-span-2 mt-2">

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF3366] hover:bg-pink-600 transition text-white py-3 rounded-xl font-medium disabled:opacity-50"
              >
                {loading
                  ? "Sending..."
                  : "Get a Demo"}
              </button>

              {/* ERROR */}
              {errors.global && (
                <p className="text-red-500 text-sm mt-3 text-center">
                  {errors.global}
                </p>
              )}

            </div>

          </form>

        </div>

      </div>
    </section>
  );
};

export default DemoForm;